<template>
  <AuthLayout>
    <template #form>
      <!-- Loading State -->
      <LoadingScreen v-if="loading" color="var(--purple)" />

      <!-- Error State -->
      <div v-else-if="error" class="consent-error">
        <AuthFormHeader title="Authorization Failed" :subtitle="errorMessage" />
        <an-link @click="goBack" class="auth-link-secondary"> Go Back </an-link>
      </div>

      <!-- Consent Form -->
      <div v-else class="consent-form">
        <AuthFormHeader
          title="Authorization Request"
          subtitle="An application is requesting access to your Anima account"
        />

        <!-- Request Info -->
        <div class="consent-info">
          <div class="info-item">
            <span class="info-label">Application</span>
            <span class="info-value client-app">
              <img v-if="clientIcon" :src="clientIcon" :alt="clientName" class="client-icon" />
              {{ clientName || 'Unknown Application' }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">Permissions</span>
            <span class="info-value">{{ formatScope(scope) }}</span>
          </div>
          <div class="info-item figma-status">
            <span class="info-label">Figma Integration</span>
            <span class="info-value">
              <span v-if="hasFigmaIntegration" class="status-badge connected">
                <svg-icon name="circle-check" :size="14" />
                Connected
              </span>
              <div v-else class="figma-connect-wrapper">
                <button
                  type="button"
                  class="connect-figma-btn"
                  title="Required for importing Figma designs"
                  @click="connectFigma"
                >
                  <img :src="require('@/assets/svg/code-icons/figma.svg')" width="16" height="16" />
                  Connect Figma
                </button>
                <span class="figma-helper-text">
                  {{ isFigmaTokenExpired ? 'Your Figma session has expired' : 'Required for importing Figma designs' }}
                </span>
              </div>
            </span>
          </div>
        </div>

        <!-- Team Selection -->
        <div class="team-selection">
          <label class="team-label">Select a team</label>
          <an-select v-model="selectedTeamId" :options="teamOptions" placeholder="Select a team" />
        </div>

        <!-- Consent Form (native HTML form for redirect) -->
        <form ref="consentForm" method="POST" :action="consentEndpoint" class="consent-actions">
          <input type="hidden" name="tx" :value="txId" />
          <input type="hidden" name="team_id" :value="selectedTeamId" />

          <an-button
            type="submit"
            name="action"
            value="approve"
            variant="purple"
            size="lg"
            :disabled="!selectedTeamId"
            :rounded="false"
          >
            Approve
          </an-button>

          <button type="submit" name="action" value="deny" class="deny-link">Deny</button>
        </form>

        <p class="consent-disclaimer">
          By approving, you allow this application to access your Anima account on behalf of the selected team.
        </p>
      </div>
    </template>
  </AuthLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import AuthLayout from '@/components/Auth/AuthLayout';
import AuthFormHeader from '@/components/Auth/AuthFormHeader';
import LoadingScreen from '@/components/Loading/LoadingScreen';
import AnSelect from '@/components/Select/Select';
import auth from '@/auth';
import { readCookie } from '@/utils/cookie';

export default {
  name: 'OAuthConsent',
  components: {
    AuthLayout,
    AuthFormHeader,
    LoadingScreen,
    'an-select': AnSelect
  },
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
      txId: '',
      clientId: '',
      clientName: '',
      scope: '',
      selectedTeamId: ''
    };
  },
  computed: {
    ...mapGetters('teamMemberships', ['userTeams']),
    ...mapGetters('users', ['figmaAuthToken', 'figmaTokenExpiresAt']),
    isFigmaTokenExpired() {
      return this.figmaAuthToken && this.figmaTokenExpiresAt && new Date(this.figmaTokenExpiresAt) <= new Date();
    },
    hasFigmaIntegration() {
      return !!this.figmaAuthToken && !this.isFigmaTokenExpired;
    },
    teamOptions() {
      return this.userTeams.map((team) => ({
        label: team.name,
        value: team.id
      }));
    },
    consentEndpoint() {
      const baseUrl = process.env.VUE_APP_PUBLIC_API_URL;
      return `${baseUrl}/oauth/consent`;
    },
    clientIcon() {
      // Map known MCP client names to their icons
      // Special case: "Claude Code" can have different suffixes (e.g., "Claude Code (anima)")
      if (this.clientName && this.clientName.startsWith('Claude Code')) {
        return require('@/assets/icons/mcp-clients/claude.svg');
      }

      const clientIcons = {
        'Visual Studio Code': require('@/assets/icons/mcp-clients/vscode.svg'),
        Cursor: require('@/assets/icons/mcp-clients/cursor.svg'),
        'Claude Code': require('@/assets/icons/mcp-clients/claude.svg')
      };
      return clientIcons[this.clientName] || null;
    }
  },
  async mounted() {
    // Check if user is authenticated
    if (!auth.isLoggedIn()) {
      // Redirect to login with return URL
      const currentUrl = window.location.href;
      this.$router.push({
        name: 'login',
        query: { redirect: currentUrl }
      });
      return;
    }

    // Get tx from query params
    this.txId = this.$route.query.tx;

    if (!this.txId) {
      this.error = true;
      this.errorMessage = 'Missing authorization request. Please start the OAuth flow again.';
      this.loading = false;
      return;
    }

    try {
      // Fetch tx info from AS
      await this.fetchTxInfo();

      // Fetch user's teams
      await this.fetchAllUserMemberships({ id: 'me', skipCache: true });

      // Pre-select current team from cookie if available
      const currentTeamId = readCookie('anima_currentTeamId');
      if (currentTeamId && this.userTeams.some((t) => t.id === currentTeamId)) {
        this.selectedTeamId = currentTeamId;
      } else if (this.userTeams.length > 0) {
        // Default to first team
        this.selectedTeamId = this.userTeams[0].id;
      }

      this.loading = false;
    } catch (err) {
      console.error('[OAuth Consent] Error:', err);
      this.error = true;
      this.errorMessage =
        err.response?.data?.error_description ||
        'Authorization request has expired or is invalid. Please start the OAuth flow again.';
      this.loading = false;
    }
  },
  methods: {
    ...mapActions('teamMemberships', ['fetchAllUserMemberships']),
    ...mapActions('figmaOAuth', ['generateFigmaOAuthUrl']),

    async fetchTxInfo() {
      const baseUrl = process.env.VUE_APP_PUBLIC_API_URL;
      const response = await axios.get(`${baseUrl}/oauth/tx-info`, {
        params: { tx: this.txId }
      });

      this.clientId = response.data.clientId;
      this.clientName = response.data.clientName;
      this.scope = response.data.scope;
    },

    formatScope(scope) {
      if (!scope) return 'Basic access';

      // Convert scope names to readable format
      const scopeDescriptions = {
        'mcp:tools': 'Access to MCP Tools'
      };

      return scopeDescriptions[scope] || scope;
    },

    goBack() {
      // Go back or redirect to home
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: 'root' });
      }
    },

    async connectFigma() {
      // Use current URL (with tx param) as callback so user returns here after Figma auth
      const callbackUrl = window.location.href;
      const figmaOAuthUrl = await this.generateFigmaOAuthUrl({ callbackUrl });
      window.location.href = figmaOAuthUrl;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_mixins.scss';
@import '@/styles/_auth.scss';
@import '@/styles/_auth-tokens.scss';

.consent-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  gap: $auth-gap-lg;

  > :first-child {
    margin-bottom: $auth-gap-sm;
  }
}

.consent-error {
  display: flex;
  flex-direction: column;
  gap: $auth-gap-lg;
  width: 100%;
  max-width: 400px;

  .auth-link-secondary {
    text-align: center;
  }
}

.consent-info {
  display: flex;
  flex-direction: column;
  gap: $auth-gap-sm;
  padding: $auth-gap-md;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .info-label {
    color: var(--transparent-white-50);
    font-size: 14px;
  }

  .info-value {
    color: var(--white);
    font-size: 14px;
    font-weight: 500;

    &.client-app {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .client-icon {
      width: 20px;
      height: 20px;
      object-fit: contain;
    }
  }
}

.team-selection {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .team-label {
    color: var(--transparent-white-70);
    font-size: 14px;
    font-weight: 500;
  }

  .an-select {
    ::v-deep .selected {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: var(--white);
      height: 48px;
      border-radius: 6px;

      &.placeholder {
        color: var(--transparent-white-50);
      }

      &::after {
        filter: invert(1);
      }
    }

    ::v-deep .options {
      background: var(--dark-card-background);
      border-color: rgba(255, 255, 255, 0.1);
      color: var(--white);

      .option {
        color: var(--white);

        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }
  }
}

.consent-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $auth-gap-sm;

  .an-button {
    width: 100%;
  }

  .deny-link {
    background: none;
    border: none;
    padding: 12px;
    cursor: pointer;
    color: var(--transparent-white-50);
    font-family: inherit;
    font-size: 14px;
    font-weight: 400;
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
      color: var(--transparent-white-70);
    }
  }
}

.consent-disclaimer {
  text-align: center;
  color: var(--transparent-white-50);
  font-size: 12px;
  line-height: 1.5;
}

.figma-status {
  align-items: flex-start !important;

  .info-label {
    padding-top: 6px;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;

    &.connected {
      background: rgba(76, 175, 80, 0.15);
      color: #4caf50;
    }
  }

  .figma-connect-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }

  .connect-figma-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    font-family: inherit;
    background: rgba(255, 152, 0, 0.1);
    border: 1px solid rgba(255, 152, 0, 0.5);
    color: #ffb74d;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 152, 0, 0.2);
      border-color: rgba(255, 152, 0, 0.7);
    }
  }

  .figma-helper-text {
    font-size: 11px;
    color: var(--transparent-white-50);
  }
}
</style>
